import React, { useState, useEffect, useMemo } from 'react';
import { Search, X, Globe, Building2, User, CreditCard, ChevronDown, ChevronRight, ExternalLink, Download, Edit, Loader, Upload, RefreshCw } from 'lucide-react';
import { Domain, DomainTransferStatus } from '../../types';
import domainsSeed from './domainsData.json';
import { domainsService } from './domainsService';
import { EditDomainModal } from './EditDomainModal';
import { useAuth } from '../../hooks/useAuth';

const AVDELING_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  'Lemon, Stavanger': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
  'Lemon, Oslo': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
  'Lemon, Tønsberg': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
  'OXI': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
  'EVE, Oslo': { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200' },
  'Oslo': { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-200' },
};

const TRANSFER_STATUS_LABELS: Record<DomainTransferStatus, { label: string; bg: string; text: string; border: string }> = {
  'keep': { label: 'Keep', bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' },
  'transfer-pending': { label: 'Pending', bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
  'transfer-in-progress': { label: 'In progress', bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
  'transferred': { label: 'Transferred', bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
};

const defaultColor = { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' };

const fullName = (fornavn: string, etternavn: string) =>
  [fornavn, etternavn].filter(Boolean).join(' ').trim();

const avdelingColor = (avdeling: string) => AVDELING_COLORS[avdeling] || defaultColor;

export const DomainsModule: React.FC = () => {
  const { user, permissions } = useAuth();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState<Domain | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [avdelingFilter, setAvdelingFilter] = useState<string>('all');
  const [innehaverFirmaFilter, setInnehaverFirmaFilter] = useState<string>('all');
  const [innehaverPersonFilter, setInnehaverPersonFilter] = useState<string>('all');
  const [betalingFirmaFilter, setBetalingFirmaFilter] = useState<string>('all');
  const [betalingPersonFilter, setBetalingPersonFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<DomainTransferStatus | 'all'>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    setLoading(true);
    try {
      const fetched = await domainsService.getDomains();
      setDomains(fetched);
    } catch (e) {
      console.error('Failed to load domains', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!user?.email) return;
    if (!window.confirm(
      `Sync ${domainsSeed.length} domains from the bundled Domeneshop CSV?\n\n` +
      `• Existing domains (matched by Domeneshop ID) will have their Domeneshop fields updated.\n` +
      `• Domains not in Firestore yet will be created.\n` +
      `• Your transfer status and internal notes will be preserved.`
    )) return;
    setSyncing(true);
    try {
      const seed = (domainsSeed as any[]).map(d => ({ ...d }));
      const result = await domainsService.syncDomeneshopFields(seed, user.email);
      await loadDomains();
      alert(`Sync complete:\n\n• Updated: ${result.updated}\n• Created: ${result.created}\n• Unchanged: ${result.unchanged}`);
    } catch (e) {
      console.error('Sync failed', e);
      alert('Sync failed. Check console for details.');
    } finally {
      setSyncing(false);
    }
  };

  const handleSeed = async () => {
    if (!window.confirm(`Import ${domainsSeed.length} domains from the bundled CSV? This is safe to do once; re-running will create duplicates.`)) return;
    setSeeding(true);
    try {
      const seed = (domainsSeed as any[]).map(d => ({
        ...d,
        transferStatus: 'keep' as DomainTransferStatus,
        internalNotes: '',
      }));
      await domainsService.seedDomains(seed);
      await loadDomains();
    } catch (e) {
      console.error('Seeding failed', e);
      alert('Import failed. Check console for details.');
    } finally {
      setSeeding(false);
    }
  };

  // Derived unique lists for filter dropdowns
  const avdelingOptions = useMemo(
    () => Array.from(new Set(domains.map(d => d.avdeling).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'nb')),
    [domains]
  );
  const innehaverFirmaOptions = useMemo(
    () => Array.from(new Set(domains.map(d => d.innehaverFirmanavn).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'nb')),
    [domains]
  );
  const innehaverPersonOptions = useMemo(
    () => Array.from(new Set(domains.map(d => fullName(d.innehaverFornavn, d.innehaverEtternavn)).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'nb')),
    [domains]
  );
  const betalingFirmaOptions = useMemo(
    () => Array.from(new Set(domains.map(d => d.betalingFirmanavn).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'nb')),
    [domains]
  );
  const betalingPersonOptions = useMemo(
    () => Array.from(new Set(domains.map(d => fullName(d.betalingFornavn, d.betalingEtternavn)).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'nb')),
    [domains]
  );

  const filtered = useMemo(() => {
    return domains.filter(d => {
      if (avdelingFilter !== 'all' && d.avdeling !== avdelingFilter) return false;
      if (innehaverFirmaFilter !== 'all' && d.innehaverFirmanavn !== innehaverFirmaFilter) return false;
      if (innehaverPersonFilter !== 'all' && fullName(d.innehaverFornavn, d.innehaverEtternavn) !== innehaverPersonFilter) return false;
      if (betalingFirmaFilter !== 'all' && d.betalingFirmanavn !== betalingFirmaFilter) return false;
      if (betalingPersonFilter !== 'all' && fullName(d.betalingFornavn, d.betalingEtternavn) !== betalingPersonFilter) return false;
      if (statusFilter !== 'all' && d.transferStatus !== statusFilter) return false;

      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return [
        d.domain, d.avdeling, d.innehaverFirmanavn, d.innehaverFornavn, d.innehaverEtternavn, d.innehaverEpost,
        d.betalingFirmanavn, d.betalingFornavn, d.betalingEtternavn, d.betalingEpost, d.betalingsreferanse, d.internalNotes
      ].some(v => (v || '').toLowerCase().includes(q));
    }).sort((a, b) => a.domain.localeCompare(b.domain, 'nb'));
  }, [domains, searchTerm, avdelingFilter, innehaverFirmaFilter, innehaverPersonFilter, betalingFirmaFilter, betalingPersonFilter, statusFilter]);

  const ownershipSummary = useMemo(() => {
    let ownsAndPays = 0, paysNotOwner = 0, ownerNotPayer = 0;
    filtered.forEach(d => {
      const innFirma = (d.innehaverFirmanavn || '').trim().toLowerCase();
      const betFirma = (d.betalingFirmanavn || '').trim().toLowerCase();
      if (innFirma && betFirma) {
        if (innFirma === betFirma) ownsAndPays++;
        else paysNotOwner++;
      } else if (innFirma && !betFirma) ownerNotPayer++;
    });
    return { ownsAndPays, paysNotOwner, ownerNotPayer };
  }, [filtered]);

  const anyFilterActive = searchTerm !== '' || avdelingFilter !== 'all' || innehaverFirmaFilter !== 'all'
    || innehaverPersonFilter !== 'all' || betalingFirmaFilter !== 'all' || betalingPersonFilter !== 'all' || statusFilter !== 'all';

  const clearAll = () => {
    setSearchTerm(''); setAvdelingFilter('all'); setInnehaverFirmaFilter('all');
    setInnehaverPersonFilter('all'); setBetalingFirmaFilter('all'); setBetalingPersonFilter('all');
    setStatusFilter('all');
  };

  const toggleExpanded = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exportCSV = () => {
    const headers = ['Domene', 'Avdeling', 'Transfer status', 'Innehaver_Firmanavn', 'Innehaver_Person', 'Betalingskontakt_Firmanavn', 'Betalingskontakt_Person', 'Internal notes'];
    const escape = (v: string) => {
      const s = (v || '').replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = filtered.map(d => [
      d.domain, d.avdeling, TRANSFER_STATUS_LABELS[d.transferStatus].label,
      d.innehaverFirmanavn, fullName(d.innehaverFornavn, d.innehaverEtternavn),
      d.betalingFirmanavn, fullName(d.betalingFornavn, d.betalingEtternavn),
      d.internalNotes,
    ].map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `domains_filtered_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin" size={32} />
        <span className="ml-2">Loading domains...</span>
      </div>
    );
  }

  // Empty state: collection hasn't been seeded yet
  if (domains.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-8 text-center space-y-4">
        <Globe size={48} className="mx-auto text-muted-foreground" />
        <div>
          <h2 className="text-xl font-semibold">No domains yet</h2>
          <p className="text-muted-foreground mt-1">
            Import the {domainsSeed.length} domains from the bundled Domeneshop CSV to get started.
          </p>
        </div>
        {permissions.canManageContent ? (
          <button
            onClick={handleSeed}
            disabled={seeding}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-50"
          >
            {seeding ? <Loader className="animate-spin" size={18} /> : <Upload size={18} />}
            {seeding ? 'Importing...' : `Import ${domainsSeed.length} domains`}
          </button>
        ) : (
          <p className="text-sm text-muted-foreground">An administrator needs to import the initial data.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Globe size={32} className="text-primary" />
          <div>
            <h2 className="text-2xl font-bold">Domains</h2>
            <p className="text-muted-foreground">{domains.length} domains registered via Domeneshop</p>
          </div>
        </div>
        <div className="flex gap-2">
          {permissions.canManageContent && (
            <button
              onClick={handleSync}
              disabled={syncing}
              className="border border-gray-300 text-foreground px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-accent disabled:opacity-50"
              title="Re-import from the bundled Domeneshop CSV. Preserves transfer status and internal notes."
            >
              {syncing ? <Loader className="animate-spin" size={18} /> : <RefreshCw size={18} />}
              {syncing ? 'Syncing...' : 'Sync from CSV'}
            </button>
          )}
          <button
            onClick={exportCSV}
            className="border border-gray-300 text-foreground px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-accent"
          >
            <Download size={18} />
            Export filtered ({filtered.length})
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border rounded-lg p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
            <Building2 size={20} className="text-green-600" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Owner = Payer</p>
            <p className="text-xl font-bold">{ownershipSummary.ownsAndPays}</p>
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
            <CreditCard size={20} className="text-amber-600" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">We pay, client owns (or other split)</p>
            <p className="text-xl font-bold">{ownershipSummary.paysNotOwner}</p>
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-50 rounded-lg flex items-center justify-center">
            <User size={20} className="text-slate-600" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Owner set, no payer</p>
            <p className="text-xl font-bold">{ownershipSummary.ownerNotPayer}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
          <input
            type="text"
            placeholder="Search domain, firm, person, email, notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10 py-3 border rounded-lg w-full text-base"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X size={20} />
            </button>
          )}
        </div>

        {/* Avdeling pills */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Avdeling</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setAvdelingFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${avdelingFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              All ({domains.length})
            </button>
            {avdelingOptions.map(a => {
              const c = avdelingColor(a);
              const count = domains.filter(d => d.avdeling === a).length;
              return (
                <button
                  key={a}
                  onClick={() => setAvdelingFilter(a)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${avdelingFilter === a ? `${c.bg} ${c.text} ${c.border}` : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  {a} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Transfer status pills */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Transfer status</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${statusFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              All
            </button>
            {(Object.keys(TRANSFER_STATUS_LABELS) as DomainTransferStatus[]).map(status => {
              const c = TRANSFER_STATUS_LABELS[status];
              const count = domains.filter(d => d.transferStatus === status).length;
              return (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${statusFilter === status ? `${c.bg} ${c.text} ${c.border}` : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  {c.label} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Dropdown filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Innehaver – Firma</label>
            <select value={innehaverFirmaFilter} onChange={(e) => setInnehaverFirmaFilter(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm">
              <option value="all">All firms</option>
              {innehaverFirmaOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Innehaver – Person</label>
            <select value={innehaverPersonFilter} onChange={(e) => setInnehaverPersonFilter(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm">
              <option value="all">All persons</option>
              {innehaverPersonOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Betalingskontakt – Firma</label>
            <select value={betalingFirmaFilter} onChange={(e) => setBetalingFirmaFilter(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm">
              <option value="all">All firms</option>
              {betalingFirmaOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Betalingskontakt – Person</label>
            <select value={betalingPersonFilter} onChange={(e) => setBetalingPersonFilter(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm">
              <option value="all">All persons</option>
              {betalingPersonOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
        </div>

        {anyFilterActive && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Showing {filtered.length} of {domains.length} domains</span>
            <button onClick={clearAll} className="text-primary hover:underline">Clear all filters</button>
          </div>
        )}
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">No domains match the current filters.</div>
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-medium text-muted-foreground bg-gray-50 border-b">
            <div className="col-span-4">Domain</div>
            <div className="col-span-2">Avdeling</div>
            <div className="col-span-3">Innehaver (eier)</div>
            <div className="col-span-3">Betalingskontakt</div>
          </div>
          <div className="divide-y">
            {filtered.map(d => {
              const c = avdelingColor(d.avdeling);
              const sc = TRANSFER_STATUS_LABELS[d.transferStatus];
              const innName = fullName(d.innehaverFornavn, d.innehaverEtternavn);
              const betName = fullName(d.betalingFornavn, d.betalingEtternavn);
              const isExpanded = expanded.has(d.id);
              const ownerIsPayer = d.innehaverFirmanavn && d.betalingFirmanavn
                && d.innehaverFirmanavn.trim().toLowerCase() === d.betalingFirmanavn.trim().toLowerCase();
              const canEdit = !!user && (permissions.canManageContent || user.email === d.updatedBy || permissions.canUploadContent);

              return (
                <div key={d.id}>
                  <button
                    onClick={() => toggleExpanded(d.id)}
                    className="w-full grid grid-cols-12 gap-2 px-4 py-3 items-center text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="col-span-4 flex items-center gap-2 min-w-0">
                      {isExpanded ? <ChevronDown size={14} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />}
                      <a
                        href={`https://${d.domain}`}
                        target="_blank" rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-sm text-blue-600 hover:underline inline-flex items-center gap-1 truncate"
                      >
                        {d.domain}
                        <ExternalLink size={11} className="flex-shrink-0" />
                      </a>
                      {d.transferStatus !== 'keep' && (
                        <span className={`px-1.5 py-0.5 text-[10px] rounded-full border flex-shrink-0 ${sc.bg} ${sc.text} ${sc.border}`}>
                          {sc.label}
                        </span>
                      )}
                      {!ownerIsPayer && d.innehaverFirmanavn && d.betalingFirmanavn && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] rounded-full border border-amber-200 flex-shrink-0">
                          split
                        </span>
                      )}
                    </div>
                    <div className="col-span-2">
                      {d.avdeling && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}>
                          {d.avdeling}
                        </span>
                      )}
                    </div>
                    <div className="col-span-3 min-w-0">
                      <div className="text-sm truncate">{d.innehaverFirmanavn || <span className="text-muted-foreground">—</span>}</div>
                      {innName && <div className="text-xs text-muted-foreground truncate">{innName}</div>}
                    </div>
                    <div className="col-span-3 min-w-0">
                      <div className="text-sm truncate">{d.betalingFirmanavn || <span className="text-muted-foreground">—</span>}</div>
                      {betName && <div className="text-xs text-muted-foreground truncate">{betName}</div>}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 pt-3 ml-6 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-dashed bg-gray-50/50">
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-muted-foreground uppercase">Innehaver</p>
                        <p className="text-sm">{d.innehaverFirmanavn || '—'}</p>
                        {d.innehaverOrgnr && <p className="text-xs text-muted-foreground">Orgnr: {d.innehaverOrgnr}</p>}
                        {innName && <p className="text-sm">{innName}</p>}
                        {d.innehaverEpost && <p className="text-xs"><a href={`mailto:${d.innehaverEpost}`} className="text-blue-600 hover:underline">{d.innehaverEpost}</a></p>}
                        {d.innehaverTelefon && <p className="text-xs text-muted-foreground">{d.innehaverTelefon}</p>}
                        {(d.innehaverPostadresse || d.innehaverPostnummer || d.innehaverSted) && (
                          <p className="text-xs text-muted-foreground">
                            {[d.innehaverPostadresse, [d.innehaverPostnummer, d.innehaverSted].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-muted-foreground uppercase">Betalingskontakt</p>
                        <p className="text-sm">{d.betalingFirmanavn || '—'}</p>
                        {d.betalingOrgnr && <p className="text-xs text-muted-foreground">Orgnr: {d.betalingOrgnr}</p>}
                        {betName && <p className="text-sm">{betName}</p>}
                        {d.betalingEpost && <p className="text-xs"><a href={`mailto:${d.betalingEpost}`} className="text-blue-600 hover:underline">{d.betalingEpost}</a></p>}
                        {d.betalingTelefon && <p className="text-xs text-muted-foreground">{d.betalingTelefon}</p>}
                        {(d.betalingPostadresse || d.betalingPostnummer || d.betalingSted) && (
                          <p className="text-xs text-muted-foreground">
                            {[d.betalingPostadresse, [d.betalingPostnummer, d.betalingSted].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {(d.fakturaType || d.fakturaValuta) && (
                          <p className="text-xs text-muted-foreground">Faktura: {[d.fakturaType, d.fakturaValuta].filter(Boolean).join(' · ')}</p>
                        )}
                        {d.betalingsreferanse && <p className="text-xs text-muted-foreground">Ref: {d.betalingsreferanse}</p>}
                      </div>

                      {d.internalNotes && (
                        <div className="md:col-span-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <p className="text-xs font-semibold text-yellow-800 uppercase mb-1">Internal notes</p>
                          <p className="text-sm whitespace-pre-wrap">{d.internalNotes}</p>
                        </div>
                      )}

                      <div className="md:col-span-2 flex items-center justify-between pt-2">
                        <div className="text-xs text-muted-foreground">
                          {d.updatedAt && d.updatedBy && (
                            <span>Last edited by {d.updatedBy} · {d.updatedAt.toLocaleDateString('nb-NO')}</span>
                          )}
                        </div>
                        {canEdit && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditing(d); }}
                            className="px-3 py-1.5 text-foreground rounded-lg flex items-center gap-1.5 hover:bg-purple-100 hover:text-purple-600 transition-colors border border-gray-200 hover:border-purple-200 text-xs"
                          >
                            <Edit size={12} /> Edit
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <EditDomainModal
        isOpen={!!editing}
        domain={editing}
        onClose={() => setEditing(null)}
        onSuccess={loadDomains}
      />
    </div>
  );
};
